import { forwardRef } from 'react'
import { useTranslation } from 'next-i18next'
import { NeutralButton } from 'shared/components/button'
import { NeutralTypeEnum } from 'shared/components/button/components/neutral-button/enum/neutral-type-enum'
import { useMediaQuery } from 'shared/hooks/use-match-media'
import CloseIcon from 'shared/icons/close-icon'
import { FileUploadIcon } from 'shared/icons/file-upload-icon'
import { LectureAssignmentFileIcon } from 'shared/icons/lecture-assignment-file-icon'
import { SuccessUploadIcon } from 'shared/icons/success-upload-icon'
import { convertBytesToMB } from 'shared/utils/convert-bytes-to-mb'
import { truncateMiddle } from 'shared/utils/truncate-middle'
import { FileInfoInterface } from 'modules/attachments/hooks/use-upload-datafile'
import { AssignmentUploadingStatusEnum } from './use-upload-assignment'

interface AssignmentUploaderProps extends React.InputHTMLAttributes<HTMLInputElement> {
  handleClick: () => void
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleDrop: (e: React.DragEvent<HTMLDivElement>) => void
  cancelUpload?: () => void
  uploadingStatus?: AssignmentUploadingStatusEnum | null
  file?: Partial<FileInfoInterface> | null
  progress?: number | null
}

const allowedFileTypes = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.apple.numbers',
  'application/pdf',
  'text/*',
]

const MAX_FILE_SIZE_MB = 10

export const AssignmentUploader = forwardRef<HTMLInputElement, AssignmentUploaderProps>(
  function AssignmentUploader(
    {
      handleClick,
      handleDrop,
      handleInputChange,
      uploadingStatus,
      file,
      progress,
      cancelUpload,
      ...props
    },
    inputRef,
  ) {
    const { t } = useTranslation()
    const isMobile = useMediaQuery('(max-width: 768px)', false)

    if (uploadingStatus === AssignmentUploadingStatusEnum.Uploading) {
      return (
        <div className="flex w-full flex-col p-1.5 md:grid md:grid-cols-2 md:p-5">
          <div className="flex items-center gap-2.5">
            <div className="flex w-full gap-2.5">
              <div className="flex items-center rounded-md border border-text-primary/30 p-2.5">
                <LectureAssignmentFileIcon className="text-THEME-secondary" />
              </div>
              <div className="flex w-full flex-1 flex-col">
                <div className="flex w-full justify-between">
                  <span className="font-semibold text-THEME-primary">
                    {truncateMiddle(file?.filename, {
                      maxLength: isMobile ? 10 : 20,
                      prefixLength: isMobile ? 3 : 10,
                      suffixLength: isMobile ? 5 : 7,
                    })}
                  </span>
                  <span className="text-THEME-primary/60 whitespace-nowrap">{progress} %</span>
                </div>

                <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-THEME-background">
                  <div
                    className="h-1 rounded-full bg-THEME-secondary"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </div>
            <NeutralButton
              neutralType={NeutralTypeEnum.danger}
              onClick={cancelUpload}
              className="border-none px-0 md:px-4"
            >
              <CloseIcon />
            </NeutralButton>
          </div>
        </div>
      )
    }

    if (uploadingStatus === AssignmentUploadingStatusEnum.Uploaded) {
      return (
        <div className="grid w-full grid-cols-1 p-1.5 md:grid-cols-2 md:p-5">
          <div className="flex w-full items-center gap-3">
            <div className="flex items-center justify-center rounded-md border border-text-primary/30 p-2.5">
              <LectureAssignmentFileIcon className="text-THEME-secondary" />
            </div>
            <div className="flex flex-1 flex-col justify-start">
              <span className="font-semibold text-THEME-primary">
                {truncateMiddle(file?.filename, {
                  maxLength: isMobile ? 10 : 20,
                  prefixLength: isMobile ? 3 : 10,
                  suffixLength: isMobile ? 5 : 7,
                })}
              </span>
              <span className="text-THEME-primary/60">{convertBytesToMB(file?.size)}</span>
            </div>
            <SuccessUploadIcon />
            <NeutralButton
              neutralType={NeutralTypeEnum.danger}
              onClick={cancelUpload}
              className="border-none"
            >
              <CloseIcon />
            </NeutralButton>
          </div>
        </div>
      )
    }

    return (
      <div
        className="flex size-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-text-primary/30 bg-THEME-background p-1.5 md:p-6"
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={e => e.preventDefault()}
      >
        <FileUploadIcon className="text-THEME-primary" />
        <span className="text-sm text-THEME-primary">{`${t('global.click_to_upload')} ${t('settings.cover.or_drag_and_drop')}`}</span>
        <span className="text-THEME-primary/60 text-xs">
          {t('course.lecture.assignment.file_upload_max_size', { size: `${MAX_FILE_SIZE_MB}MB` })}
        </span>
        <input
          type="file"
          ref={inputRef}
          accept={allowedFileTypes.join(',')}
          className="hidden"
          onChange={handleInputChange}
          {...props}
        />
      </div>
    )
  },
)
